import React, { memo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import { BiCopy } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";
import PropTypes from "prop-types";

import ChartFetch from "@/components/HomeCharts/ChartFetch";
import DialGuegeLayout from "@/components/LayoutCharts/DialGuegeLayout";
import Layout3DTank from "@/components/LayoutCharts/Layout3DTank";
import LayoutCounter from "@/components/LayoutCharts/LayoutCounter";
import LayoutDynamicBox from "@/components/LayoutCharts/LayoutDynamicBox";
import LayoutLoadSummary from "@/components/LayoutCharts/LayoutLoadSummary";
import LayoutSplitCounter from "@/components/LayoutCharts/LayoutSplitCounter";
import LayoutTextBox from "@/components/LayoutCharts/LayoutTextBox";
import TableChart from "@/components/LayoutCharts/TableChart";
import Thermometer from "@/components/LayoutCharts/Thermometer";
import DashboardImage from "@/components/LayoutCharts/DashboardImage";
import LayoutAlarmNotification from "@/components/LayoutCharts/LayoutAlarmNotification";

const ResponsiveReactGridLayout = WidthProvider(Responsive);
const calculateNextPosition = (layout, itemWidth = 3, itemHeight = 3, maxX = 16) => {
  // Find the maximum y position for existing items
  let maxY = 0;

  // Track grid occupancy
  const grid = {};

  layout.forEach((item) => {
    for (let i = 0; i < item.w; i++) {
      for (let j = 0; j < item.h; j++) {
        const posX = item.x + i;
        const posY = item.y + j;
        grid[`${posX},${posY}`] = true;
      }
    }
    maxY = Math.max(maxY, item.y + item.h);
  });

  // Find the next position where the new item can fit
  for (let y = 0; y <= maxY + 1; y++) {
    for (let x = 0; x <= maxX - itemWidth; x++) {
      let fits = true;

      // Check if the item can fit at this position
      for (let i = 0; i < itemWidth; i++) {
        for (let j = 0; j < itemHeight; j++) {
          if (grid[`${x + i},${y + j}`]) {
            fits = false;
            break;
          }
        }
        if (!fits) break;
      }

      if (fits) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: maxY + 1 };
};

const DashboardLayout = memo(
  ({
    gridData,
    setGridData,
    setLayouts,
    layouts,
    mode,
    setEditItem = () => {},
    setComponentModal = () => {},
    selectedTiles = [],
    handleSelect = () => {},
    mainDashboard = false,
  }) => {
    const { isDark } = useSelector((state) => state.theme);
    const { currentSite, currentSiteSelectedTiles } = useSelector((state) => state.dashboard);

    // states
    const [dialChangeKey, setDialChangeKey] = useState(new Date());
    const [currBP, setCurrBP] = useState("lg");

    // const onLayoutChange = (layout, data) => {
    const onLayoutChange = (_, data) => {
      setLayouts(data);
      setDialChangeKey(new Date());
    };

    const handlerRemove = (id) => {
      const newData = gridData.filter((data) => data._id !== id);
      setGridData(newData);
      const newLayout = layouts?.[currBP]?.filter((layout) => String(layout.i) !== String(id));
      setLayouts({ [currBP]: newLayout });
    };

    const handlerUpdate = (item) => {
      setEditItem(item);
      setComponentModal(true);
    };

    const defaultLayout = {
      w: 3,
      h: 3,
      x: 0,
      y: 0,
      moved: false,
      static: false,
    };

    const handleCopy = (item) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to copy this tile?.",
        dangerMode: true,
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          const _id = uuid();
          setGridData([
            ...gridData,
            {
              ...item,
              _id,
              chartTitle: item.chartTitle + " (copy)",
            },
          ]);

          const nextPosition = calculateNextPosition(layouts?.[currBP] || []);

          setLayouts((prev) => ({
            [currBP]: [...prev[currBP], { ...defaultLayout, i: _id, ...nextPosition }],
          }));
        }
      });
    };

    const handleRemove = (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to remove this tile?.",
        dangerMode: true,
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          handlerRemove(id);
        }
      });
    };

    return (
      <div key={currBP}>
        <ResponsiveReactGridLayout
          className={`layout ${mode !== "create" && (isDark ? "bg-dark" : "bg-white")} ${
            mode === "view" ? " client_view" : "create"
          }`}
          id="layout1"
          rowHeight={80}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          draggableCancel=".clickBtn"
          isDraggable={mode === "view" ? false : true}
          isResizable={mode === "view" ? false : true}
          breakpoints={{ lg: 900, md: 700, sm: 500, xs: 400, xxs: 300 }}
          cols={{ lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 }}
          // eslint-disable-next-line no-unused-vars
          onBreakpointChange={(breakpoint, a) => {
            // console.log(breakpoint, layouts?.lg?.[1]);
            setCurrBP(breakpoint);
          }}
        >
          {gridData?.map((i, idx) => {
            let item = { ...i };
            if (selectedTiles.length && selectedTiles.includes(item?._id)) {
              let items = [];
              currentSiteSelectedTiles?.forEach((tileId) => {
                const component = currentSite.items?.find((tile) => tile?._id === tileId);
                if (component) {
                  items.push(component);
                }
              });
              if (items.length) {
                item.locations = items[0]?.locations;
                item.operator = items[0]?.customOperator;
                item.xAxis = items[0]?.customXAxis;
              }
            }

            return (
              <div
                className={`dashboardTiles ${item.isBorderHidden ? "" : "withBorder"} ${
                  selectedTiles.includes(item?._id) ? "active" : ""
                }`}
                key={item._id}
                data-grid={layouts?.[currBP]?.[idx] || defaultLayout}
                onDoubleClick={() => {
                  if (item.component !== "textBox") handleSelect(item?._id);
                }}
              >
                <div className="position-relative h-100 d-flex align-items-center">
                  {mode !== "view" ? (
                    <div
                      className="d-flex justify-content-end align-items-center position-absolute"
                      style={{ top: 10, right: 15, zIndex: 10 }}
                    >
                      <div className="clickBtn" onClick={() => handleCopy(item)}>
                        <BiCopy className="text-secondary me-2" />
                      </div>
                      <div className="clickBtn" onClick={() => handlerUpdate(item)}>
                        <FiEdit className="text-secondary me-1" />
                      </div>
                      <div className="clickBtn" onClick={() => handleRemove(item._id)}>
                        <MdDeleteForever className="fs-5 text-secondary" />
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  {["bar", "line", "pie", "comboBar"].includes(item.component) ? (
                    <div className="p-2 h-100 w-100">
                      <ChartFetch
                        data={item}
                        type={item.component}
                        mainDashboard={mainDashboard}
                        isSelected={selectedTiles.includes(item?._id)}
                      />
                    </div>
                  ) : item.component === "dial" ? (
                    <DialGuegeLayout data={item} dialChangeKey={dialChangeKey} mainDashboard={mainDashboard} />
                  ) : // <RedialChartLayout data={item}/>
                  item.component === "splitCounter" ? (
                    <LayoutSplitCounter data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "textBox" ? (
                    <LayoutTextBox data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "html" ? (
                    <LayoutTextBox data={item} />
                  ) : item.component === "tank" ? (
                    <Layout3DTank data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "dynamicBox" ? (
                    <LayoutDynamicBox data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "loadSummary" ? (
                    <LayoutLoadSummary data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "thermometer" ? (
                    <Thermometer data={item} mainDashboard={mainDashboard} />
                  ) : item.component === "table" ? (
                    <TableChart {...{ data: item, mainDashboard }} />
                  ) : item.component === "image" ? (
                    <DashboardImage {...{ data: item }} />
                  ) : item.component === "alarmNotification" ? (
                    <LayoutAlarmNotification data={item} />
                  ) : (
                    item.component === "counter" && <LayoutCounter data={item} mainDashboard={mainDashboard} />
                  )}
                </div>
              </div>
            );
          })}
        </ResponsiveReactGridLayout>
      </div>
    );
  },
);

export default DashboardLayout;

DashboardLayout.displayName = "DashboardLayout";

DashboardLayout.propTypes = {
  gridData: PropTypes.array,
  setGridData: PropTypes.func,
  setLayouts: PropTypes.func,
  layouts: PropTypes.array,
  mode: PropTypes.string,
  setEditItem: PropTypes.func,
  setComponentModal: PropTypes.func,
  selectedTiles: PropTypes.array,
  handleSelect: PropTypes.func,
  mainDashboard: PropTypes.bool,
};
